(() => {
  const SELECTORS = {
    hero: '[data-skeptics-hero]',
    reasons: '[data-skeptics-reasons]',
    reason: '[data-skeptics-reason]',
    reasonClip: '[data-skeptics-reason-clip]',
    progress: '[data-skeptics-progress]',
    label: '[data-skeptics-progress-label]',
    segment: '[data-skeptics-progress-segment]',
    builder: '[data-skeptics-builder], #skeptics-builder',
    builderBox: '.build-a-bundle__info-container'
  };

  let cleanupCurrentPage = null;
  let cleanupLagomStandalone = null;

  const initialiseBuilderPagination = (builder) => {
    if (!builder || builder.dataset.skepticsPagination !== 'true') return () => {};

    const cardSelector = [
      '.build-a-bundle__product-card',
      '.how-it-works-bab-card',
      '.whats-with-blocks-bab-card',
      '.taste-guarantee-bab-card'
    ].join(',');
    const cards = [...builder.querySelectorAll(cardSelector)];
    const viewMoreButton = builder.querySelector('[data-skeptics-view-more]');

    // The grid is 2 columns on mobile and 3 from the theme's `desktop:`
    // breakpoint up, so the batch size has to change with the breakpoint or
    // "View more" leaves a part-filled last row on desktop. The cards the LP
    // hides in the desktop grid drop out of the count too: counting a
    // display:none card would render a row of 3 as a row of 2.
    const desktopQuery = window.matchMedia(
      `(min-width: ${Math.max(1, Number(builder.dataset.skepticsDesktopBreakpoint) || 1024)}px)`
    );
    let desktopHiddenSelector = (builder.dataset.skepticsDesktopHiddenCards || '').trim();
    if (desktopHiddenSelector) {
      try {
        document.createDocumentFragment().querySelector(desktopHiddenSelector);
      } catch (error) {
        desktopHiddenSelector = '';
      }
    }

    // Each collection's heading sits inside the same grid as its cards but is
    // not a card, so pagination never touched it: with 28 Adults cards ahead of
    // it, every one of the 8 Kids cards fell behind the first-batch cut while
    // the "Kids" title stayed on screen as an orphaned heading above
    // "View more". Group the cards by collection so a heading can be hidden
    // while its whole collection is behind the cut.
    const collections = [...builder.querySelectorAll('.build-a-bundle__collection')]
      .map((root) => ({
        root,
        // The marker is rendered by lp-lagomchef-build-a-bundle.liquid; the
        // fallback finds the same wrapper structurally (product cards carry an
        // h3 of their own, so cards have to be excluded) in case the snippet
        // and this asset are ever out of step on the live theme.
        heading: root.querySelector('[data-skeptics-collection-heading]')
          || [...root.children].find((child) => !child.matches(cardSelector) && child.querySelector('h3')),
        cards: cards.filter((card) => root.contains(card)),
        noMatches: root.querySelector('.build-a-bundle__collection-no-filter-matches')
      }))
      .filter((collection) => collection.heading && collection.cards.length);

    const readCount = (value, fallback) => Math.max(1, Number(value) || fallback);

    let pagedCards = cards;
    let initialCount = 10;
    let increment = 10;
    // Counted in clicks, not cards, so a breakpoint change keeps the customer
    // roughly where they were instead of collapsing the grid back to one batch.
    let expansions = 0;
    let renderQueued = false;

    if (!cards.length || !viewMoreButton) return () => {};

    const readBreakpoint = () => {
      const isDesktop = desktopQuery.matches;

      initialCount = isDesktop
        ? readCount(builder.dataset.skepticsInitialCountDesktop, 9)
        : readCount(builder.dataset.skepticsInitialCount, 10);
      increment = isDesktop
        ? readCount(builder.dataset.skepticsCardIncrementDesktop, 9)
        : readCount(builder.dataset.skepticsCardIncrement, 10);
      pagedCards = isDesktop && desktopHiddenSelector
        ? cards.filter((card) => !card.matches(desktopHiddenSelector))
        : cards;
    };

    const render = () => {
      renderQueued = false;
      const visibleLimit = initialCount + (expansions * increment);
      const filterVisibleCards = pagedCards.filter((card) => !card.classList.contains('hidden'));

      cards.forEach((card) => {
        const filteredIndex = filterVisibleCards.indexOf(card);
        const shouldHide = filteredIndex === -1 || filteredIndex >= visibleLimit;
        card.classList.toggle('skeptics-card--page-hidden', shouldHide);
      });

      collections.forEach((collection) => {
        const hasVisibleCard = collection.cards.some((card) => (
          !card.classList.contains('hidden') && !card.classList.contains('skeptics-card--page-hidden')
        ));
        // A collection filtered down to nothing still shows the theme's "no
        // products match" line, and that line needs its title to say which
        // collection it is about. Only a collection emptied by pagination
        // loses its heading.
        const showsNoMatches = collection.noMatches
          ? !collection.noMatches.classList.contains('hidden')
          : false;
        const isOrphaned = !hasVisibleCard && !showsNoMatches;
        collection.heading.classList.toggle('skeptics-collection-heading--page-hidden', isOrphaned);
        collection.root.classList.toggle('skeptics-collection--page-empty', isOrphaned);
      });

      const remainingCount = Math.max(0, filterVisibleCards.length - visibleLimit);
      viewMoreButton.hidden = remainingCount === 0;
      viewMoreButton.textContent = remainingCount > increment
        ? `View ${increment} more`
        : remainingCount > 0
          ? `View ${remainingCount} more`
          : 'View more';
      viewMoreButton.setAttribute('aria-expanded', remainingCount === 0 ? 'true' : 'false');
    };

    const queueRender = () => {
      if (renderQueued) return;
      renderQueued = true;
      window.requestAnimationFrame(render);
    };

    const onViewMore = () => {
      expansions += 1;
      render();
    };

    const onBreakpointChange = () => {
      readBreakpoint();
      render();
    };

    // The Adults/Kids anchor buttons scroll to their collection, but cards
    // beyond the pagination cut are display:none, so the Kids section would
    // measure near-empty and the scroll would land wrong. Capture phase runs
    // on this ancestor BEFORE the theme's own listener on the button itself,
    // so the full grid is laid out by the time the theme computes offsets.
    const onNavCapture = (event) => {
      if (!event.target.closest('.build-a-bundle__sticky-nav-trigger')) return;
      const totalCards = pagedCards.filter((card) => !card.classList.contains('hidden')).length;
      if (initialCount + (expansions * increment) >= totalCards) return;
      expansions = Math.ceil((totalCards - initialCount) / increment);
      render();
    };

    const classObserver = new MutationObserver(queueRender);
    cards.forEach((card) => classObserver.observe(card, { attributes: true, attributeFilter: ['class'] }));
    viewMoreButton.addEventListener('click', onViewMore);
    builder.addEventListener('click', onNavCapture, true);
    if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', onBreakpointChange);
    else if (desktopQuery.addListener) desktopQuery.addListener(onBreakpointChange);
    readBreakpoint();
    render();

    return () => {
      classObserver.disconnect();
      viewMoreButton.removeEventListener('click', onViewMore);
      builder.removeEventListener('click', onNavCapture, true);
      if (desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', onBreakpointChange);
      else if (desktopQuery.removeListener) desktopQuery.removeListener(onBreakpointChange);
      cards.forEach((card) => card.classList.remove('skeptics-card--page-hidden'));
      collections.forEach((collection) => {
        collection.heading.classList.remove('skeptics-collection-heading--page-hidden');
        collection.root.classList.remove('skeptics-collection--page-empty');
      });
    };
  };

  const initialiseGuaranteeQuotes = () => {
    const swipe = document.querySelector('[data-skeptics-guarantee-swipe]');
    const track = swipe?.querySelector('[data-skeptics-guarantee-track]');
    const quotes = swipe ? [...swipe.querySelectorAll('[data-skeptics-guarantee-quote]')] : [];
    const dots = swipe ? [...swipe.querySelectorAll('[data-skeptics-guarantee-dot]')] : [];
    let updateQueued = false;

    if (!track || quotes.length < 2 || quotes.length !== dots.length) return () => {};

    const setActiveDot = (activeIndex) => {
      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const updateFromScroll = () => {
      updateQueued = false;
      const activeQuote = quotes.reduce((closest, quote, index) => {
        const distance = Math.abs(quote.offsetLeft - track.scrollLeft);
        return distance < closest.distance ? { index, distance } : closest;
      }, { index: 0, distance: Number.POSITIVE_INFINITY });
      setActiveDot(activeQuote.index);
    };

    const onScroll = () => {
      if (updateQueued) return;
      updateQueued = true;
      window.requestAnimationFrame(updateFromScroll);
    };

    const dotHandlers = dots.map((dot, index) => {
      const handler = () => track.scrollTo({
        left: quotes[index].offsetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
      dot.addEventListener('click', handler);
      return handler;
    });

    track.addEventListener('scroll', onScroll, { passive: true });
    setActiveDot(0);

    return () => {
      track.removeEventListener('scroll', onScroll);
      dots.forEach((dot, index) => dot.removeEventListener('click', dotHandlers[index]));
    };
  };

  const initialiseDelegatedPopups = () => {
    // Rows outside the builder's template root (e.g. the Dragons' Den card's
    // delivery row) cannot be bound by app.js, whose popup triggers are scoped
    // to .build-a-bundle-template. Delegate their clicks to the builder's own
    // trigger so the theme's popup machinery stays the single owner.
    const delegates = [...document.querySelectorAll('[data-skeptics-delegated-popup]')];
    if (!delegates.length) return () => {};

    const handlers = delegates.map((el) => {
      const handler = (event) => {
        const target = el.dataset.skepticsDelegatedPopup;
        const trigger = document.querySelector(`.build-a-bundle-template .${target}-popup-trigger`);
        if (!trigger) return; // no popup on the page: an <a> delegate falls back to its href
        event.preventDefault();
        trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      };
      el.addEventListener('click', handler);
      return handler;
    });

    return () => {
      delegates.forEach((el, index) => el.removeEventListener('click', handlers[index]));
    };
  };

  // initialiseCustomiseBundle was removed on 2026-08-25 after the production
  // review: "Customise bundle" became "Build your bundle" and now opens an
  // EMPTY builder, so the four-pick seed it existed to perform is gone. Grace
  // raised that removing an unwanted pack is hard on desktop and Andrea that a
  // prefilled box fights how personal food is; Tim ruled the collab page plays
  // it safe while the Families and Sceptics LPs keep Customise bundle and carry
  // the AOV read. Recover it from snapshots/2026-08-25-lagomchef-feedback-pass/
  // if that ruling is ever reversed.

  const initialiseBundleItemPopups = () => {
    // Tapping an item inside the bundle box opens the same meal popup as the
    // builder's product cards: the tap is relayed to that card's own
    // popup trigger, so the theme's popup machinery stays the single owner.
    // Delegated from the box container because the theme re-renders the item
    // rows on every bundle mutation.
    const builder = document.querySelector('.build-a-bundle-template');
    const box = builder?.querySelector('.build-a-bundle__info-container');
    if (!builder || !box) return () => {};

    const openPopupForVariant = (variantId) => {
      if (!variantId) return;
      const card = builder.querySelector(`.build-a-bundle__product-card[data-id="${variantId}"]`);
      card?.querySelector('.build-a-bundle__product-card__popup-trigger')
        ?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    };

    const handler = (event) => {
      if (event.target.closest('button, a')) return; // leave the row's real controls alone

      const mobileItem = event.target.closest('.build-a-bundle__info-body-mobile-product-list-product');
      if (mobileItem) {
        openPopupForVariant(mobileItem.dataset.productId);
        return;
      }

      // The desktop thumbnails carry no id, only the item's image, so match
      // the src back to the bundle entry that rendered it.
      const thumb = event.target.closest('.desktop-bundle-image');
      if (thumb) {
        const src = thumb.querySelector('img')?.getAttribute('src');
        if (!src) return;
        const entry = Object.entries(window.bundle?.items || {}).find(([, item]) => item?.image === src);
        openPopupForVariant(entry?.[0]);
      }
    };

    box.addEventListener('click', handler);
    return () => box.removeEventListener('click', handler);
  };

  const initialiseReasonClips = () => {
    // Each reason's media can be a silent looping clip cut from that reason's
    // own source ad. Playback is scoped to the row being on screen so five
    // videos never decode at once, and the poster (the clip's own first frame)
    // covers every case where playback does not happen: reduced motion, a
    // refused autoplay, a failed load.
    const clips = [...document.querySelectorAll(SELECTORS.reasonClip)];
    if (!clips.length) return () => {};

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onScreen = new Set();

    const play = (clip) => {
      if (motionQuery.matches) return;
      clip.muted = true; // the attribute alone does not satisfy every autoplay policy
      const started = clip.play();
      started?.catch?.(() => {}); // refusal is fine: the poster stays up
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          onScreen.add(entry.target);
          play(entry.target);
        } else {
          onScreen.delete(entry.target);
          entry.target.pause();
        }
      });
    }, { threshold: 0.25 });

    clips.forEach((clip) => {
      clip.muted = true;
      observer.observe(clip);
    });

    // A backgrounded tab gets no intersection callbacks, so pause on the way
    // out and restart whatever was on screen on the way back in.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') clips.forEach((clip) => clip.pause());
      else onScreen.forEach(play);
    };

    const onMotionChange = () => {
      if (motionQuery.matches) clips.forEach((clip) => clip.pause());
      else onScreen.forEach(play);
    };

    document.addEventListener('visibilitychange', onVisibility);
    motionQuery.addEventListener?.('change', onMotionChange);

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      motionQuery.removeEventListener?.('change', onMotionChange);
    };
  };

  const initialiseSkepticsPage = () => {
    if (cleanupCurrentPage) cleanupCurrentPage();

    const hero = document.querySelector(SELECTORS.hero);
    const reasonsRoot = document.querySelector(SELECTORS.reasons);
    const progress = reasonsRoot?.querySelector(SELECTORS.progress);
    const reasons = reasonsRoot ? [...reasonsRoot.querySelectorAll(SELECTORS.reason)] : [];

    if (!hero || !reasonsRoot || !progress || !reasons.length) return;

    if (progress.parentElement !== document.body) document.body.append(progress);

    const label = progress.querySelector(SELECTORS.label);
    const segments = [...progress.querySelectorAll(SELECTORS.segment)];
    const builder = document.querySelector(SELECTORS.builder);
    const cleanupPagination = initialiseBuilderPagination(builder);
    const cleanupGuaranteeQuotes = initialiseGuaranteeQuotes();
    const cleanupDelegatedPopups = initialiseDelegatedPopups();
    const cleanupBundleItemPopups = initialiseBundleItemPopups();
    const cleanupReasonClips = initialiseReasonClips();
    const mediaQuery = window.matchMedia('(max-width: 1024px)');
    let currentReason = 1;
    let heroIsVisible = true;
    let reasonsInView = false;
    let builderIsVisible = false;
    let scrollTickQueued = false;

    const setActiveReason = (nextIndex) => {
      currentReason = Math.max(1, Math.min(reasons.length, nextIndex));
      if (label) label.textContent = `Reason ${currentReason} of ${reasons.length}`;

      segments.forEach((segment, segmentIndex) => {
        const displayedIndex = segmentIndex + 1;
        segment.classList.toggle('is-active', displayedIndex === currentReason);
        segment.classList.toggle('is-complete', displayedIndex < currentReason);
        if (displayedIndex === currentReason) segment.setAttribute('aria-current', 'step');
        else segment.removeAttribute('aria-current');
      });
    };

    // The active reason is the last one whose top has crossed a fixed trigger
    // line, recomputed from geometry on every scroll frame. A pure function of
    // scroll position updates in both directions with no ratchet, and with
    // reasons around a viewport tall a single line needs no hysteresis: the
    // label can only flicker on sub-pixel jitter exactly at a boundary.
    // The midline, not the scrollspy-conventional near-top (~25%) or the
    // viewport bottom: a reason goes active as it takes over the upper half of
    // the screen, where mobile reading attention sits, so the counter agrees
    // with what is under the eye at the moment it changes; the bottom edge
    // would show "5 of 5" while reason 4 still fills the screen. Measured
    // against visualViewport so the mobile address-bar collapse doesn't drift
    // the line.
    const REASON_TRIGGER_LINE = 0.5;

    const syncActiveReason = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const triggerY = viewportHeight * REASON_TRIGGER_LINE;
      let nextIndex = 1;
      reasons.forEach((reason, index) => {
        if (reason.getBoundingClientRect().top <= triggerY) {
          nextIndex = Number(reason.dataset.reasonIndex) || index + 1;
        }
      });
      setActiveReason(nextIndex);
    };

    const syncProgressVisibility = () => {
      // The bar lives and dies with the reasons section: visible only while some
      // part of it is on screen. The hero gate stops a flash at page top, where
      // the hero and the first reason can share the viewport.
      const shouldShow = reasonsInView && !heroIsVisible;
      progress.classList.toggle('is-visible', shouldShow);
      progress.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    };

    const bundleHasItems = () => {
      const items = window.bundle?.items;
      if (!items) return false;
      return Object.values(items).some((item) => Number(item?.quantity) > 0);
    };

    // The theme's expanded bundle box locks the page (body position:fixed via
    // its lockBackground), and only its own close path unlocks it. So the box
    // must never be hidden while expanded: close it through the theme's own
    // trigger first, and if that somehow fails, leave the box visible rather
    // than strand the shopper on an unscrollable page. (Bug found by Tim:
    // emptying the bundle from inside the expanded box hid it mid-lock.)
    const closeExpandedBuilderBox = (builderBox) => {
      if (!builderBox.classList.contains('active')) return true;
      const trigger = builder.querySelector('.build-a-bundle__info-expand-trigger')
        || builder.querySelector('.build-a-bundle__info-header-trigger');
      trigger?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      return !builderBox.classList.contains('active');
    };

    const syncBuilderBox = () => {
      if (!builder) return;
      const builderBox = builder.querySelector(SELECTORS.builderBox);
      if (!builderBox) return;

      if (mediaQuery.matches) {
        // Once the bundle holds anything, the sticky box stays visible across
        // the whole page so the shopper never loses their bundle; empty
        // bundles keep the original builder-in-view gating.
        const shouldShow = builderIsVisible || bundleHasItems();
        if (!shouldShow && !closeExpandedBuilderBox(builderBox)) return;
        builderBox.classList.toggle('skeptics-builder-box--in-view', shouldShow);
      } else {
        if (!closeExpandedBuilderBox(builderBox)) return;
        builderBox.classList.remove('skeptics-builder-box--in-view');
      }
    };

    const heroObserver = new IntersectionObserver(([entry]) => {
      heroIsVisible = entry.isIntersecting;
      syncProgressVisibility();
    }, { threshold: 0.05 });

    const reasonsRootObserver = new IntersectionObserver(([entry]) => {
      reasonsInView = entry.isIntersecting;
      if (reasonsInView) syncActiveReason();
      syncProgressVisibility();
    }, { threshold: 0 });

    const builderObserver = builder ? new IntersectionObserver(([entry]) => {
      builderIsVisible = entry.isIntersecting;
      syncBuilderBox();
    }, { threshold: 0.02 }) : null;

    // The theme re-renders the bundle box's contents on every bundle mutation
    // (adds, removes, localStorage preload, the customise-bundle seeding), so
    // its subtree changing is the signal that bundleHasItems() may have
    // flipped. syncBuilderBox only toggles a class on the box element itself,
    // which this observer does not watch, so no feedback loop.
    const builderBoxElement = builder?.querySelector(SELECTORS.builderBox);
    const builderBoxObserver = builderBoxElement ? new MutationObserver(syncBuilderBox) : null;
    builderBoxObserver?.observe(builderBoxElement, { childList: true, subtree: true, characterData: true });

    const onScroll = () => {
      if (!reasonsInView || scrollTickQueued) return;
      scrollTickQueued = true;
      window.requestAnimationFrame(() => {
        scrollTickQueued = false;
        syncActiveReason();
      });
    };

    heroObserver.observe(hero);
    reasonsRootObserver.observe(reasonsRoot);
    if (builderObserver && builder) builderObserver.observe(builder);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    const onViewportChange = () => syncBuilderBox();
    mediaQuery.addEventListener?.('change', onViewportChange);
    syncActiveReason();
    syncProgressVisibility();
    syncBuilderBox();

    cleanupCurrentPage = () => {
      heroObserver.disconnect();
      reasonsRootObserver.disconnect();
      builderObserver?.disconnect();
      builderBoxObserver?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      mediaQuery.removeEventListener?.('change', onViewportChange);
      cleanupPagination();
      cleanupGuaranteeQuotes();
      cleanupDelegatedPopups();
      cleanupBundleItemPopups();
      cleanupReasonClips();
      if (reasonsRoot.isConnected) reasonsRoot.append(progress);
      else progress.remove();
      cleanupCurrentPage = null;
    };
  };


  /* =====================================================================
     Martyn's Picks (strict families fork, 2026-08-23): the bundle-card
     machinery ported from lp-families.js with lagomchef- data attributes.
     initialiseSkepticsPage early-returns on this page (no reasons section),
     so these run standalone, mirroring the families wiring.
     ===================================================================== */
  const initialiseBundleEquations = () => {
    const sections = [...document.querySelectorAll('.section-prebuilt-bundles')];
    if (!sections.length) return () => {};

    const observers = sections.map((section) => {
      const equations = [...section.querySelectorAll('.lagomchef-prebuilt__equation')];
      const titles = [...section.querySelectorAll('h3.bundle-title[data-product-id]')];
      if (!equations.length || !titles.length) return null;

      const sync = () => {
        const active = titles.find((t) => t.classList.contains('active'));
        if (!active) return;
        equations.forEach((eq) => {
          eq.classList.toggle('active', eq.dataset.productId === active.dataset.productId);
        });
      };

      const observer = new MutationObserver(sync);
      titles.forEach((t) => observer.observe(t, { attributes: true, attributeFilter: ['class'] }));
      sync();
      return observer;
    }).filter(Boolean);

    return () => observers.forEach((o) => o.disconnect());
  };

  const initialiseInfoPopups = () => {
    // These are the dedicated production Build Your Bundle disclosure popups,
    // rendered up front with the same class contract as the production snippets.
    // app.js scopes its click bindings to .build-a-bundle-template, so this LP
    // copies only its state transition: add .active to open, remove it to close.
    const popups = [...document.querySelectorAll('[data-lagomchef-info-popup]')];
    if (!popups.length) return () => {};

    let activePopup = null;
    let activeTrigger = null;

    const lockBackground = () => {
      if (document.body.style.position === 'fixed') return;
      document.body.classList.add('overflow-y-hidden');
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    };

    const unlockBackground = () => {
      if (document.body.style.position !== 'fixed') return;
      document.body.classList.remove('overflow-y-hidden');
      const scrollY = Math.abs(parseInt(document.body.style.top || 0, 10));
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
      });
    };

    const close = ({ restoreFocus = true } = {}) => {
      if (!activePopup) return;
      activePopup.classList.remove('active');
      activePopup = null;
      unlockBackground();
      if (restoreFocus && activeTrigger?.isConnected) activeTrigger.focus({ preventScroll: true });
      activeTrigger = null;
    };

    const open = (key, trigger) => {
      const popup = popups.find((candidate) => candidate.dataset.lagomchefInfoPopup === key);
      if (!popup) return false;
      if (activePopup && activePopup !== popup) activePopup.classList.remove('active');
      activePopup = popup;
      activeTrigger = trigger;
      lockBackground();
      popup.classList.add('active');
      popup.querySelector('button[data-lagomchef-info-popup-close]')?.focus({ preventScroll: true });
      return true;
    };

    const onClick = (event) => {
      const trigger = event.target.closest('[data-lagomchef-delegated-popup]');
      if (trigger) {
        if (open(trigger.dataset.lagomchefDelegatedPopup, trigger)) event.preventDefault();
        return;
      }

      const closeControl = event.target.closest('[data-lagomchef-info-popup-close]');
      if (closeControl?.closest('[data-lagomchef-info-popup]') === activePopup) close();
    };

    const onKeydown = (event) => {
      if (event.key === 'Escape' && activePopup) close();
    };

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);

    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeydown);
      close({ restoreFocus: false });
    };
  };

  const initialiseDishRowPopups = () => {
    // Tapping a dish row in a prebuilt bundle card opens that product's details,
    // the same content the build-a-bundle page shows on a product card.
    //
    // The builder is not on this LP, so there is no card to delegate a click to.
    // What IS here is the theme's popup shell: one .popup-container holding a
    // .popup-contents-output, opened by adding .active while the body takes
    // overflow-y-hidden (verified by firing an existing trigger and diffing the
    // DOM). The section renders the builder's own popup-contents snippet once per
    // unique product; this copies the right one into that shell. Guarantee and
    // delivery use their own production-anatomy overlays and are intentionally
    // owned by initialiseInfoPopups instead.
    const rows = [...document.querySelectorAll('[data-lagomchef-product-popup-trigger]')];
    const container = document.querySelector('.popup-container');
    const shell = container?.querySelector('.popup-content-container');
    if (!rows.length || !shell) return () => {};

    // The theme's popup is a MULTI-PANEL shell: .popup-content-container holds one
    // .popup-contents per popup on the page, every one display:none, and opening
    // reveals the matching panel. Writing into an existing .popup-contents-output
    // therefore renders into a dormant panel and shows nothing (measured: output
    // 0x0 because its .popup-contents parent computes display:none).
    // So the LP brings its own panel and reuses everything else the shell gives
    // us: the fixed positioning, the backdrop and the theme's own close handling.
    const getPanel = () => {
      let panel = shell.querySelector('[data-lagomchef-product-panel]');
      if (!panel) {
        panel = document.createElement('div');
        panel.className = 'popup-contents';
        panel.setAttribute('data-lagomchef-product-panel', '');
        // One delegated listener on the panel, not a bind per control per open:
        // the panel outlives every clone, so this survives replaceChildren and
        // never has to be torn down between products.
        panel.addEventListener('click', onPanelClick);
        shell.appendChild(panel);
      }
      return panel;
    };

    const hidePanel = () => {
      const panel = shell.querySelector('[data-lagomchef-product-panel]');
      if (!panel) return;
      panel.style.display = 'none';
      panel.replaceChildren(); // drop the clone so its images can be collected
    };

    const panelIsOpen = () => shell.querySelector('[data-lagomchef-product-panel]')?.style.display === 'block';

    // Mirrors app.js contentLinks lockBackground/unlockBackground exactly. The
    // shell's backdrop was bound at init to the theme's closePopups, which ends
    // in its unlockBackground, and that restores scroll by reading
    // document.body.style.top. Opening with nothing but the overflow class left
    // that inline style empty, so the theme parsed 0 and threw the shopper to the
    // top of the page: measured on the live LP, scrollY 4099 before opening and 0
    // after a backdrop tap. Setting the same inline styles the theme sets means
    // its own unlock restores the right position, and per the theme's comment it
    // is also the only lock iOS Safari honours.
    const lockBackground = () => {
      document.body.classList.add('overflow-y-hidden');
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    };

    const unlockBackground = () => {
      // Idempotent on purpose: whichever unlock runs second must do nothing.
      // Both unlocks recover the scroll position by parsing body.style.top, so a
      // second run would read the emptied string, parse 0 and throw the shopper
      // to the top of the page.
      if (document.body.style.position !== 'fixed') return;
      document.body.classList.remove('overflow-y-hidden');
      const scrollY = Math.abs(parseInt(document.body.style.top || 0, 10));
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
      });
    };

    // Our unlock always yields to the theme's, because on the backdrop path they
    // both run and only the FIRST one restores the right position. Measured on
    // the live LP: our .popup-bg listener fires before the theme's (the LP script
    // boots on DOMContentLoaded, app.js binds contentLinks later), so unlocking
    // inline gave two scrollTo calls, 4099 then 0, and the shopper landed at the
    // top of the page. The theme's closePopups is fully synchronous, so a
    // macrotask is enough to let it win; our call then hits the guard above and
    // does nothing. On the paths the theme knows nothing about (our close button,
    // Escape) nothing else clears body.style.top, so this still restores 4099,
    // one tick later.
    const deferUnlockBackground = () => {
      window.setTimeout(unlockBackground, 0);
    };

    const close = () => {
      container.classList.remove('active');
      hidePanel();
      deferUnlockBackground();
    };

    // The theme's close buttons live inside its own .popup-contents panels and
    // are bound per element at init (app.js: querySelectorAll('.close-button')
    // .forEach(addEventListener)), so a clone inherits markup but never a
    // handler. Borrowing the markup keeps it pixel-identical to every other
    // popup on the store; the handler is ours. It is position:absolute against
    // .popup-content-container, which is our panel's PARENT rather than the panel
    // itself, so it stays pinned while the panel scrolls under it (measured:
    // panel scrollHeight 1271 vs clientHeight 822, so the panel does scroll).
    const buildCloseButton = () => {
      const themeButton = [...shell.querySelectorAll('.popup-contents .close-button')]
        .find((button) => !button.closest('[data-lagomchef-product-panel]'));
      const button = themeButton ? themeButton.cloneNode(true) : document.createElement('button');
      if (!themeButton) {
        // Last resort so the panel can never be unclosable. This LP's shell ships
        // six theme panels to borrow from, but a content-links section rendered
        // with no blocks would ship none.
        button.className = 'close-button z-50 absolute -top-3.5 right-1/2 transform translate-x-1/2 w-11 h-11 flex flex-col justify-center items-center';
        button.textContent = '✕';
      }
      button.setAttribute('type', 'button');
      button.setAttribute('aria-label', 'Close');
      button.setAttribute('data-lagomchef-product-panel-close', '');
      return button;
    };

    // Every control below was measured dead on the live page before this handler
    // existed: the close button was absent entirely, and the nutrition table
    // toggle, the four block multipliers and all five accordions did nothing when
    // clicked. The cause is the same for all of them: app.js binds each control
    // once at init, and this panel is cloned out of a <template> long afterwards.
    // Each branch replicates the theme's own handler rather than inventing new
    // behaviour, so the clone behaves like the build-a-bundle popup it came from.
    const onPanelClick = (event) => {
      if (event.target.closest('[data-lagomchef-product-panel-close]')) {
        close();
        return;
      }

      // app.js productNutritionInfo: toggle `hidden` on the dropdown and
      // `-rotate-180` on the trigger's chevron. Same classes, so the theme's
      // existing transition on that svg applies unchanged.
      const tableToggle = event.target.closest('.nutrition-table-toggle');
      if (tableToggle) {
        const dropdown = tableToggle.closest('.nutrition-table-container')?.querySelector('.nutrition-table-dropdown');
        if (!dropdown) return;
        const willOpen = dropdown.classList.contains('hidden');
        dropdown.classList.toggle('hidden', !willOpen);
        tableToggle.querySelector('svg')?.classList.toggle('-rotate-180', willOpen);
        return;
      }

      // app.js productNutritionInfo again: the rendered figures are already
      // multiplied by the theme's default, so the per-block value has to come
      // from data-original-value, and the rounding has to match the theme's or
      // the same tap would give different decimals here and on the builder.
      const multiplier = event.target.closest('.product-nutrition-info-content__multiplier-value');
      if (multiplier) {
        if (multiplier.classList.contains('active')) return;
        multiplier.closest('.product-nutrition-info-content__multiplier-container')
          ?.querySelectorAll('.product-nutrition-info-content__multiplier-value')
          .forEach((option) => option.classList.remove('active'));
        multiplier.classList.add('active');

        const factor = parseInt(multiplier.textContent, 10);
        if (!factor) return;
        multiplier.closest('.product-nutrition-info-content')
          ?.querySelectorAll('.product-nutrition-metric')
          .forEach((metric) => {
            const originalValue = metric.dataset.originalValue;
            const valueSpan = metric.querySelector('.product-nutrition-metric-value');
            if (!originalValue || !valueSpan) return;
            valueSpan.textContent = Math.round(originalValue * factor * 100) / 100;
          });
        return;
      }

      // app.js productAccordions. Its judgeme branch is deliberately not copied:
      // it fires only for a `.judgeme-review-accordion` trigger, and
      // snippets/product-accordion.liquid never emits that class, so the branch
      // is unreachable in the theme too.
      const accordionTrigger = event.target.closest('.product-accordion__trigger');
      if (accordionTrigger) {
        accordionTrigger.closest('.product-accordion')?.classList.toggle('active');
      }
    };

    const open = (productId) => {
      // A <template>: its content is inert, so the galleries inside cost nothing
      // until this clone runs. Read .content, not .children.
      const source = document.querySelector(`template[data-lagomchef-product-popup="${productId}"]`);
      const contents = source?.content?.firstElementChild?.cloneNode(true);
      if (!contents) return;

      // Strip the add-to-bundle bar. It is the only part of the builder's popup
      // that needs the builder's JS, which is not on this page, so it would sit
      // there as dead controls. Its container's parent IS the fixed bottom bar
      // that holds the quantity selector and every Add to Bundle variant.
      contents.querySelector('.bab-product-popup-contents__atb-container')?.parentElement?.remove();

      showPanel(contents);
    };

    const showPanel = (contents) => {
      const panel = getPanel();
      panel.replaceChildren(buildCloseButton(), contents);
      panel.style.display = 'block';
      container.classList.add('active');
      lockBackground();
    };

    const onClick = (event) => {
      // Never swallow a real control inside the row (quantity steppers when the
      // card is rendered with include_quantity on).
      if (event.target.closest('button, a, input, select')) return;
      const row = event.target.closest('[data-lagomchef-product-popup-trigger]');
      if (!row) return;
      open(row.dataset.lagomchefProductPopupTrigger);
    };

    const onKeydown = (event) => {
      // Escape is ours to add: the theme's shell has no keyboard route out at
      // all. Gated on our own panel being the visible one so this never closes a
      // theme popup, whose behaviour is not ours to change.
      if (event.key === 'Escape') {
        if (panelIsOpen()) close();
        return;
      }
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const row = event.target.closest('[data-lagomchef-product-popup-trigger]');
      if (!row || event.target.closest('button, a, input, select')) return;
      event.preventDefault();
      open(row.dataset.lagomchefProductPopupTrigger);
    };

    // The theme already binds the backdrop, and with lockBackground in place that
    // path now closes us correctly. This is insurance, not duplication: the
    // theme's bind is conditional on the section having rendered at least one
    // popup block (app.js: `if ($popupBg && $popUps.length > 0)`), so a shell
    // with no theme panels would leave the backdrop inert. Both handlers running
    // is harmless because close() is idempotent.
    const backdrop = container.querySelector('.popup-bg');
    const onBackdropClick = () => {
      if (panelIsOpen()) close();
    };
    backdrop?.addEventListener('click', onBackdropClick);

    // Our panel is the only one that is not display:none by default, so it has to
    // be put away again or it would sit under any popup the theme opens next.
    // Two ways out: the theme drops .active on the container when it closes
    // (its own close button and backdrop both do this), and a theme trigger fired
    // while ours is open is about to reveal a different panel. The theme-trigger
    // path deliberately does NOT unlock: the theme's openPopup locks on the very
    // next tick, and unlocking in between would clear body.style.top so that lock
    // would record scroll position 0.
    const onThemeTrigger = (event) => {
      if (event.target.closest('.popup-trigger')) hidePanel();
    };
    // The catch-all for the defect the client reported: whatever drops .active,
    // the body gets its scroll back. Deferred for the same reason close() is: an
    // observer callback is a microtask, so it would otherwise beat the theme's
    // own unlock to body.style.top on any path the theme is also handling.
    const activeObserver = new MutationObserver(() => {
      if (!container.classList.contains('active')) {
        hidePanel();
        deferUnlockBackground();
      }
    });
    activeObserver.observe(container, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('click', onThemeTrigger, true);

    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('click', onThemeTrigger, true);
      backdrop?.removeEventListener('click', onBackdropClick);
      shell.querySelector('[data-lagomchef-product-panel]')?.removeEventListener('click', onPanelClick);
      activeObserver.disconnect();
      hidePanel();
      // A section reload while the popup is open must not leave the body locked:
      // the observer is disconnected by this point, so nothing else would.
      unlockBackground();
    };
  };

  const initialiseSeedBundle = () => {
    // Every CTA whose intent is "just the Tagine" seeds the Build a Bundle
    // builder with that ONE meal and then lets its own href navigate, so the
    // shopper arrives with it already in the box and builds the rest on top.
    // Same cross-page mechanism as the families LP (assets/lp-families.js
    // seedAcrossPages): the builder rebuilds itself from localStorage.bundle on
    // load, keyed by VARIANT id, so a correctly shaped write before navigation
    // is the whole trick.
    //
    // Bound by an OPT-IN attribute, data-lagomchef-seed-meal, not by destination.
    // It was destination-bound until the 25 Aug review, where Andrea ruled that a
    // cold visitor should not be thrown into a prefilled cart before they know
    // what STOCKED is: only a CTA that literally sells the dish may seed it. The
    // attribute is emitted by the two dish sections (ingredients x2, street x2);
    // the generic "Shop now" CTAs (hero, why, waste, closer) point at the same
    // builder and deliberately arrive with an empty box, as does the prebuilt
    // card's "Build your bundle".
    //
    // The href is still checked, so repointing a dish CTA somewhere else cannot
    // leave a stale seed behind on a page the builder never loads.
    //
    // The meal comes from one page-level payload the hero renders, so there is a
    // single source of truth for WHICH meal: change the hero's Seed setting and
    // every CTA follows. No payload means no seeding, and every CTA still
    // navigates.
    //
    // DEMO CAVEAT 2026-08-25: Chicken Tagine has no selling plan group, and the
    // builder adds every line with the Dynamic Box Subscription plan, so
    // /cart/add.js answers 422 "Cannot apply selling plan to variant" and the
    // cart stays empty with nothing shown to the shopper. The seed, the box and
    // the pricing are all correct up to that point. Two fixes are owed by
    // Stocked: put the product in the plan group, and in a builder collection so
    // it also renders as a card. Until then these CTAs demo the journey only.
    const payloadNode = document.getElementById('LagomChefBundleSeed');
    if (!payloadNode) return () => {};

    let seed = null;
    try {
      seed = JSON.parse(payloadNode.textContent);
    } catch (error) {
      return () => {}; // malformed payload: leave every CTA as a plain link
    }
    if (!seed || !seed.id) return () => {};

    const triggers = [...document.querySelectorAll('a[data-lagomchef-seed-meal]')]
      .filter((anchor) => (anchor.getAttribute('href') || '').includes('/products/build-a-bundle'));
    if (!triggers.length) return () => {};

    // Mirror the theme's setupBundleObject field for field, including its
    // parseFloat treatment, so the builder cannot tell our write from its own.
    const item = {
      title: seed.title,
      price: parseFloat(seed.price),
      priceString: seed.priceString,
      image: seed.image,
      quantity: 1
    };
    if (parseFloat(seed.comparePrice) > 0) {
      item.comparePrice = parseFloat(seed.comparePrice);
      item.comparePriceString = seed.comparePriceString;
    }

    const handler = () => {
      // REPLACE, not merge (ADR-0005), and it reads nothing back: a fresh
      // bundleId every time makes it idempotent under double-taps and the back
      // button, and a clean object clears any editing_bundle flag left by an
      // abandoned subscription edit.
      try {
        window.localStorage.setItem('bundle', JSON.stringify({
          bundleId: `bundle_${Date.now()}`,
          items: { [String(seed.id)]: item }
        }));
      } catch (error) {
        // Private mode or a full quota: the anchor still navigates, the shopper
        // just lands on an empty builder rather than a broken one.
      }
      // No preventDefault: the href carries them to the builder.
    };

    triggers.forEach((trigger) => trigger.addEventListener('click', handler));

    return () => {
      triggers.forEach((trigger) => trigger.removeEventListener('click', handler));
    };
  };

  // Image warm-up (2026-09-02, Tim on an iPhone: accordion photos arrived late on
  // open and most images landed after they were on screen). Native loading="lazy"
  // is what keeps hidden desktop duplicates and closed popups from loading, but
  // Safari's own lazy distance is short, and no observer margin can reach a slide
  // parked off-screen inside a horizontal carousel: the carousel's own overflow
  // clips it out of every intersection test. So the trigger is the SECTION: once a
  // section is within 1200px of the viewport, every lazy image in it that is
  // actually displayed flips to eager (verified in WebKit: changing loading to
  // eager starts the fetch). display:none duplicates and closed popups have no
  // offsetParent, stay lazy, and load only when shown, so the load-time saving is
  // untouched; everything a reader is about to reach is already fetched, and a
  // collapsed accordion photo is ready before the item opens.
  const initialiseImageWarmup = () => {
    if (!('IntersectionObserver' in window)) return () => {};
    const sections = [...document.querySelectorAll('.shopify-section')].filter((section) => section.querySelector('img[loading="lazy"]'));
    if (!sections.length) return () => {};
    const wake = (section) => {
      section.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        if (image.offsetParent) image.loading = 'eager';
      });
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) wake(entry.target);
      });
    }, { rootMargin: '1200px 0px' });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  };

  const initialiseLazyVideos = () => {
    const videos = [...document.querySelectorAll('video[data-lp-lazy-video]')];
    if (!videos.length) return () => {};

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Two steps, deliberately. The poster is cheap and is what a reader sees first,
    // so every displayed clip in a section gets it as soon as the section is near.
    // Sources are attached only when the clip itself is in view: with HLS sources
    // WebKit buffers an attached clip whether or not it is playing, and attaching
    // a whole carousel at once made ten seconds on Using your Blocks pull ~100MB
    // (measured 2026-09-02). Poster first, sources on intersection, load() after
    // the sources so the theme's pending play() promise is rejected and nothing
    // starts on its own.
    // The content-links hover clips exist for the desktop mouseenter effect only
    // (the theme's own handler gates on innerWidth > 1023); on a phone they sit at
    // opacity 0 under the main image, so attaching them there is pure waste.
    const isPhoneOnlyHidden = (video) => video.classList.contains('hover-video') && window.innerWidth <= 1023;

    const attachPoster = (video) => {
      if (isPhoneOnlyHidden(video)) return;
      if (video.dataset.lpPoster === 'true') return;
      video.dataset.lpPoster = 'true';
      if (video.dataset.poster) video.setAttribute('poster', video.dataset.poster);
    };

    const attach = (video) => {
      if (isPhoneOnlyHidden(video)) return;
      attachPoster(video);
      if (video.dataset.lpAttached === 'true') return;
      video.dataset.lpAttached = 'true';
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.setAttribute('src', source.dataset.src);
      });
      video.load();
    };

    const play = (video) => {
      if (video.dataset.lpAutoplay === undefined || motionQuery.matches) return;
      video.muted = true;
      const started = video.play();
      started?.catch?.(() => {});
    };

    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => { attach(video); play(video); });
      return () => {};
    }

    // Reaching one clip attaches sources and poster to every displayed clip in
    // the same section. A carousel's off-screen slides are clipped by its own
    // overflow, so no margin ever reaches them (found on an iPhone, 2026-09-02:
    // the Using your Blocks slides to the right never loaded). Attaching costs
    // nothing but the poster with preload="none"; only play() fetches, and that
    // still happens per clip, when it is actually intersecting.
    const attachGroup = (video) => {
      const section = video.closest('.shopify-section');
      if (!section) return;
      section.querySelectorAll('video[data-lp-lazy-video]').forEach((other) => {
        if (other.offsetParent) attachPoster(other);
      });
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          attach(video);
          attachGroup(video);
          play(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    }, { rootMargin: '800px 0px' });

    videos.forEach((video) => observer.observe(video));

    // Section-level safety net (2026-09-02, Tim on an iPhone: Using your Blocks and
    // the footer clip showed nothing on the Skeptics LP while Playwright's WebKit
    // showed them fine). Anything that creates <video> elements after boot, a
    // carousel cloning its slides on a real touch device for example, leaves
    // clips this observer has never seen. So each section that holds lazy clips
    // is watched too: when it comes within 800px, every lazy clip inside it,
    // clones included, is attached, and any clip not yet observed is handed to the
    // per-clip observer above so playback still follows the viewport.
    const observed = new WeakSet(videos);
    const sections = [...new Set(videos.map((video) => video.closest('.shopify-section')).filter(Boolean))];
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.querySelectorAll('video[data-lp-lazy-video]').forEach((video) => {
          if (video.offsetParent) attachPoster(video);
          if (!observed.has(video)) {
            observed.add(video);
            observer.observe(video);
          }
        });
      });
    }, { rootMargin: '800px 0px' });
    sections.forEach((section) => sectionObserver.observe(section));

    return () => {
      observer.disconnect();
      sectionObserver.disconnect();
    };
  };


  // Opt-in on-device diagnostics (2026-09-02): open the page with ?lpdebug=1 and
  // every uncaught error and rejected promise is printed in a fixed overlay, so a
  // phone-only failure can be read without a cable. Inert without the parameter.
  const lpDebug = /[?&]lpdebug=1/.test(window.location.search);
  const lpReport = (label, error) => {
    if (!lpDebug) return;
    let box = document.getElementById('lp-debug');
    if (!box) {
      box = document.createElement('pre');
      box.id = 'lp-debug';
      box.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;max-height:40vh;overflow:auto;margin:0;padding:8px;background:#111;color:#0f0;font:11px/1.4 monospace;white-space:pre-wrap;';
      document.body.appendChild(box);
    }
    const message = error && (error.stack || error.message) ? (error.stack || error.message) : String(error);
    box.textContent += '[' + label + '] ' + message + '\n';
  };
  if (lpDebug) {
    window.addEventListener('error', (event) => lpReport('error', event.error || event.message));
    window.addEventListener('unhandledrejection', (event) => lpReport('rejection', event.reason));
    document.addEventListener('DOMContentLoaded', () => lpReport('boot', 'script ran, UA ' + navigator.userAgent.slice(0, 80)));
  }

  // One initialiser failing must not take the others with it: run each in its own
  // try/catch and return a no-op cleanup for the one that failed.
  const guarded = (label, initialise) => {
    try {
      return initialise() || (() => {});
    } catch (error) {
      lpReport(label, error);
      return () => {};
    }
  };

  const initialiseLagomStandalone = () => {
    cleanupLagomStandalone?.();
    const cleanups = [
      guarded('lazyVideos', initialiseLazyVideos),
      guarded('imageWarmup', initialiseImageWarmup),
      guarded('infoPopups', initialiseInfoPopups),
      guarded('dishRowPopups', initialiseDishRowPopups),
      guarded('seedBundle', initialiseSeedBundle),
      guarded('bundleEquations', initialiseBundleEquations)
    ];
    cleanupLagomStandalone = () => {
      cleanups.forEach((cleanup) => cleanup?.());
      cleanupLagomStandalone = null;
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseLagomStandalone, { once: true });
  } else {
    initialiseLagomStandalone();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseSkepticsPage, { once: true });
  } else {
    initialiseSkepticsPage();
  }

  document.addEventListener('shopify:section:load', initialiseSkepticsPage);
  document.addEventListener('shopify:section:load', initialiseLagomStandalone);
  document.addEventListener('shopify:section:unload', () => {
    cleanupCurrentPage?.();
    cleanupLagomStandalone?.();
  });
})();
